import React, { useState } from 'react';

import { getMonthlyEquivalent, getSavePercentage, pricingPlans } from '../Config/PricingPlans';

const PlanCard = ({ plan, isHighlighted, onMouseEnter, onMouseLeave, onSubscribe, buttonLabel, disabled, validationError, isActivatingTrial }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Get corresponding monthly plan price for savings calculation
  const getMonthlyPlanPrice = () => {
    const monthlyPlan = pricingPlans.find(
      p => p.name === plan.name && p.billingCycle === 'MONTHLY'
    );
    return monthlyPlan?.price || null;
  };

  const monthlyEquivalent = plan.isYearly && plan.price ? getMonthlyEquivalent(plan.price) : null;
  const savesPercentage = plan.isYearly && plan.price ? getSavePercentage(plan.price, getMonthlyPlanPrice()) : null;
  return (
    <div
      className={`relative rounded-2xl p-6 sm:p-8 w-full min-h-[600px] flex flex-col transition-all duration-200 cursor-pointer bg-white ${
        isHighlighted
          ? 'ring-2 ring-purple-500 shadow-2xl scale-105'
          : 'shadow-lg'
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {plan.popular && (
        <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-6 sm:mb-8">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
        <p className="text-sm sm:text-base text-gray-600 mb-4 px-2">{plan.description}</p>
        <div className="flex items-baseline justify-center">
          <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800">{plan.priceDisplay}</span>
          <span className="text-gray-600 ml-2 text-sm sm:text-base">{plan.period}</span>
        </div>
        {plan.isYearly && monthlyEquivalent && (
          <p className="text-sm text-gray-500 mt-2">
            ~{monthlyEquivalent} per month
          </p>
        )}
        {savesPercentage && (
          <div className="mt-2">
            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded font-bold">
              save {savesPercentage}
            </span>
          </div>
        )}
      </div>

      <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow">
        {plan.features.map((feature, featureIndex) => (
          <li key={featureIndex} className="flex items-start">
            <i className="ri-check-line text-green-500 text-lg sm:text-xl mr-2 sm:mr-3 flex-shrink-0 mt-0.5"></i>
            <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{feature}</span>
          </li>
        ))}
      </ul>

      {plan.includesText && (
        <>
          <div className="border-t border-gray-200 my-4"></div>
          <p className="text-sm font-semibold text-purple-600 mb-4">{plan.includesText}</p>
        </>
      )}

      <div 
        className="relative"
        onMouseEnter={() => disabled && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <button
          className={`w-full font-bold rounded-3xl py-2.5 mt-4 shadow-xl flex items-center justify-center gap-2 text-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400 ${
            disabled
              ? 'bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white cursor-not-allowed opacity-50'
              : 'bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white hover:scale-105 hover:shadow-2xl hover:from-purple-700 hover:via-blue-700 hover:to-indigo-800'
          }`}
          onClick={() => !disabled && onSubscribe(plan.name)}
          disabled={disabled}
        >
          <span className="flex items-center gap-2">
            {isActivatingTrial && plan.name === 'Free Trial' ? (
              <>
                <div className="w-5 h-5 border-[3px] border-white/30 border-t-white rounded-full animate-spin"></div>
                Activating…
              </>
            ) : (
              <>
                {!disabled && (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5"/></svg>
                )}
                {buttonLabel || plan.buttonText || 'Subscribe'}
              </>
            )}
          </span>
        </button>
        {disabled && showTooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50 pointer-events-none">
            {buttonLabel === 'Current Plan' ? 'You are currently on this plan' : 'Downgrade not available'}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanCard;
